import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { adminApiService } from '../../services/adminApi';
import { ResendDeathTileResponse, UserReferralRewardsResponse, UserData, ProbLossConfig, ReferredUsersResponse, ReferredUserWithStats } from '../../types';
import { ApiError } from '../../services/api';
import styles from '../../styles/GameManagement.module.css';
import { ethers } from 'ethers';

interface GameOperation {
  id: string;
  gameId: string;
  operation: 'resend-death-tile';
  status: 'pending' | 'success' | 'error';
  result?: ResendDeathTileResponse;
  error?: string;
  timestamp: Date;
}

const GameManagement: React.FC = () => {
  const {
    triggerStreakReset,
    streakResetLoading,
    streakResetError,
    streakResetSuccess,
    clearStreakResetError,
    clearStreakResetSuccess,
    triggerDrawCompletion,
    drawCompletionLoading,
    drawCompletionError,
    drawCompletionSuccess,
    clearDrawCompletionError,
    clearDrawCompletionSuccess,
    triggerLeaderboardSnapshot,
    leaderboardSnapshotLoading,
    leaderboardSnapshotError,
    leaderboardSnapshotSuccess,
    clearLeaderboardSnapshotError,
    clearLeaderboardSnapshotSuccess,
  } = useAdmin();
  
  const [gameId, setGameId] = useState('');
  const [drawDate, setDrawDate] = useState('');
  const [leaderboardWeek, setLeaderboardWeek] = useState('');
  const [loading, setLoading] = useState(false);
  const [operations, setOperations] = useState<GameOperation[]>([]);

  // User Management state
  const [walletAddress, setWalletAddress] = useState('');
  const [userRewardsLoading, setUserRewardsLoading] = useState(false);
  const [userRewardsData, setUserRewardsData] = useState<UserReferralRewardsResponse | null>(null);
  const [userRewardsError, setUserRewardsError] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userDataLoading, setUserDataLoading] = useState(false);
  
  // Mark editing states
  const [editingMark, setEditingMark] = useState(false);
  const [markInput, setMarkInput] = useState('');
  const [markLoading, setMarkLoading] = useState(false);

  // Claim blocked toggle state
  const [claimBlockedLoading, setClaimBlockedLoading] = useState(false);

  // ProbLoss config editing states
  const [editingProbLoss, setEditingProbLoss] = useState(false);
  const [probLossLoading, setProbLossLoading] = useState(false);
  const [probLossForm, setProbLossForm] = useState<ProbLossConfig>({
    ruleType: 'multiplier_based',
    multiplierThreshold: 15,
    lossProbability: 0.5,
    notes: '',
  });

  // Multiple search results state
  const [searchResults, setSearchResults] = useState<UserData[]>([]);

  // Referred users modal states
  const [showReferredUsersModal, setShowReferredUsersModal] = useState(false);
  const [referredUsersData, setReferredUsersData] = useState<ReferredUsersResponse | null>(null);
  const [referredUsersLoading, setReferredUsersLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showBatchProbLossForm, setShowBatchProbLossForm] = useState(false);
  const [batchProbLossForm, setBatchProbLossForm] = useState<ProbLossConfig>({
    ruleType: 'multiplier_based',
    multiplierThreshold: 15,
    lossProbability: 0.5,
    notes: '',
  });
  const [batchProbLossLoading, setBatchProbLossLoading] = useState(false);
  const [batchProbLossError, setBatchProbLossError] = useState<string | null>(null);
  const [batchProbLossSuccess, setBatchProbLossSuccess] = useState<string | null>(null);

  const isValidGameId = (id: string): boolean => {
    // Basic validation - should be a non-empty string
    // You can add more specific validation based on your game ID format
    return id.trim().length > 0 && id.length <= 100;
  };

  const isValidWalletAddress = (address: string): boolean => {
    // Validate Ethereum address format
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  const isValidSearchInput = (input: string): boolean => {
    // Valid if it's either a valid wallet address OR a mark search (at least 1 character)
    return isValidWalletAddress(input) || (input.trim().length >= 1);
  };

  const addOperation = (operation: Omit<GameOperation, 'id' | 'timestamp'>): string => {
    const newOperation: GameOperation = {
      ...operation,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
    };
    
    setOperations(prev => [newOperation, ...prev.slice(0, 9)]); // Keep last 10 operations
    return newOperation.id;
  };

  const updateOperation = (id: string, updates: Partial<GameOperation>) => {
    setOperations(prev => 
      prev.map(op => op.id === id ? { ...op, ...updates } : op)
    );
  };

  const handleResendDeathTile = async () => {
    if (!isValidGameId(gameId)) {
      alert('Please enter a valid Game ID');
      return;
    }

    setLoading(true);
    
    const operationId = addOperation({
      gameId: gameId.trim(),
      operation: 'resend-death-tile',
      status: 'pending',
    });

    try {
      const result = await adminApiService.resendDeathTile(gameId.trim());
      
      updateOperation(operationId, {
        status: 'success',
        result,
      });

      // Clear the input on success
      setGameId('');
      
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Unknown error occurred';
      
      updateOperation(operationId, {
        status: 'error',
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const clearOperations = () => {
    setOperations([]);
  };

  const isValidDrawDate = (date: string): boolean => {
    if (!date) return true; // Empty date is valid (defaults to previous day)
    
    // Check date format YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
    
    // Check that date is not today or in the future
    const today = new Date().toISOString().split('T')[0];
    return date < today;
  };

  const handleDrawCompletion = async () => {
    if (drawDate && !isValidDrawDate(drawDate)) {
      return; // Invalid date, don't proceed
    }

    const targetDate = drawDate || 'previous day';
    const confirmed = window.confirm(
      `Are you sure you want to complete the draw for ${targetDate}?\n\n` +
      'This action will:\n' +
      '• Complete the specified draw and select winners\n' +
      '• Process all pending entries for that date\n' +
      '• Cannot be undone'
    );
    
    if (confirmed) {
      try {
        await triggerDrawCompletion(drawDate || undefined);
        // Clear the input on success
        setDrawDate('');
      } catch (error) {
        // Error handling is done in the hook
      }
    }
  };

  const handleStreakReset = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to reset streaks for all users who didn\'t play yesterday?\n\n' +
      'This action will:\n' +
      '• Set currentStreak to 0 for inactive users\n' +
      '• This affects their leaderboard multipliers\n' +
      '• Cannot be undone'
    );
    
    if (confirmed) {
      await triggerStreakReset();
    }
  };

  const isValidLeaderboardWeek = (date: string): boolean => {
    if (!date) return true; // Empty date is valid (defaults to current week)
    
    // Check date format YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
    
    // Check that date is a Monday (for weekly leaderboards)
    const dateObj = new Date(date + 'T00:00:00');
    return dateObj.getDay() === 1; // Monday is day 1
  };

  const handleLeaderboardSnapshot = async () => {
    if (leaderboardWeek && !isValidLeaderboardWeek(leaderboardWeek)) {
      return; // Invalid date, don't proceed
    }

    const targetWeek = leaderboardWeek || 'current week';
    const confirmed = window.confirm(
      `Are you sure you want to generate leaderboard snapshot for ${targetWeek}?\n\n` +
      'This action will:\n' +
      '• Create a snapshot of the leaderboard rankings\n' +
      '• Mark the leaderboard as completed (inactive)\n' +
      '• Trigger automatic reward calculation\n' +
      '• Cannot be undone'
    );
    
    if (confirmed) {
      try {
        await triggerLeaderboardSnapshot(leaderboardWeek || undefined);
        // Clear the input on success
        setLeaderboardWeek('');
      } catch (error) {
        // Error handling is done in the hook
      }
    }
  };

  const handleSearchUserRewards = async () => {
    if (!isValidSearchInput(walletAddress)) {
      setUserRewardsError('Please enter a wallet address or mark to search');
      return;
    }

    setUserRewardsLoading(true);
    setUserDataLoading(true);
    setUserRewardsError(null);
    setUserRewardsData(null);
    setUserData(null);
    setSearchResults([]);

    try {
      if (isValidWalletAddress(walletAddress)) {
        // Direct wallet address lookup
        const [rewardsData, userInfo] = await Promise.all([
          adminApiService.getUserReferralRewards(walletAddress.trim()),
          adminApiService.getUser(walletAddress.trim())
        ]);
        
        setUserRewardsData(rewardsData);
        setUserData(userInfo);
        setSearchResults([]); // Clear multiple results when showing single user
      } else {
        // Search by mark field
        const searchResults = await adminApiService.searchUsers(walletAddress.trim());
        
        if (searchResults.length === 0) {
          setUserRewardsError('No users found with that mark');
          return;
        }
        
        if (searchResults.length > 1) {
          // Show all matching users without referral data
          setSearchResults(searchResults);
          setUserRewardsData(null);
          setUserData(null);
          setUserRewardsError(null);
          return;
        }

        // Found exactly one user, fetch their rewards data
        const foundUser = searchResults[0];
        
        try {
          const rewardsData = await adminApiService.getUserReferralRewards(foundUser.walletAddress);
          setUserRewardsData(rewardsData);
        } catch (rewardsError) {
          // If referral rewards fetch fails, we still have the user data from search
          console.warn('Failed to fetch referral rewards for user:', rewardsError);
          setUserRewardsData({
            walletAddress: foundUser.walletAddress,
            totalRewards: "0",
            paidRewards: "0", 
            unpaidRewards: "0",
            referredUsers: 0,
            totalBetAmount: "0",
            totalRewardsBNB: "0",
            paidRewardsBNB: "0",
            unpaidRewardsBNB: "0", 
            totalBetAmountBNB: "0",
            canClaim: false,
            referralRatio: foundUser.referralRatio || 2,
            isVIP: false
          });
        }
        
        setUserData(foundUser);
        setSearchResults([]); // Clear multiple results when showing single user
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to fetch user data';
      setUserRewardsError(errorMessage);
    } finally {
      setUserRewardsLoading(false);
      setUserDataLoading(false);
    }
  };

  const clearUserRewardsData = () => {
    setUserRewardsData(null);
    setUserData(null);
    setUserRewardsError(null);
    setSearchResults([]);
    setWalletAddress('');
    // Reset mark editing state
    setEditingMark(false);
    setMarkInput('');
  };

  const handleEditMark = () => {
    setMarkInput(userData?.mark || '');
    setEditingMark(true);
  };

  const handleSaveMark = async () => {
    if (!userData) return;

    setMarkLoading(true);
    try {
      const trimmedMark = markInput.trim();
      const markValue = trimmedMark === '' ? null : trimmedMark;
      
      const updatedUser = await adminApiService.updateUserMark(userData.walletAddress, markValue);
      setUserData(updatedUser);
      setEditingMark(false);
      setMarkInput('');
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to update mark';
      setUserRewardsError(errorMessage);
    } finally {
      setMarkLoading(false);
    }
  };

  const handleCancelEditMark = () => {
    setEditingMark(false);
    setMarkInput('');
  };

  const handleToggleClaimBlocked = async () => {
    if (!userData) return;

    setClaimBlockedLoading(true);
    try {
      const newClaimBlocked = !userData.claimBlocked;
      const updatedUser = await adminApiService.updateUserClaimBlocked(
        userData.walletAddress,
        newClaimBlocked
      );
      setUserData(updatedUser);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to update claim blocked status';
      setUserRewardsError(errorMessage);
    } finally {
      setClaimBlockedLoading(false);
    }
  };

  /**
   * Start editing probLoss configuration
   */
  const handleEditProbLoss = () => {
    if (!userData) return;

    // Initialize form with current config or defaults
    if (userData.probLossConfig) {
      setProbLossForm({
        ...userData.probLossConfig,
      });
    } else {
      // Default to multiplier-based rule
      setProbLossForm({
        ruleType: 'multiplier_based',
        multiplierThreshold: 15,
        lossProbability: 0.5,
        notes: '',
      });
    }

    setEditingProbLoss(true);
  };

  /**
   * Cancel probLoss editing
   */
  const handleCancelProbLoss = () => {
    setEditingProbLoss(false);
    setProbLossForm({
      ruleType: 'multiplier_based',
      multiplierThreshold: 15,
      lossProbability: 0.5,
      notes: '',
    });
  };

  /**
   * Save probLoss configuration
   */
  const handleSaveProbLoss = async () => {
    if (!userData) return;

    // Validate form inputs
    const validationError = validateProbLossForm(probLossForm);
    if (validationError) {
      setUserRewardsError(validationError);
      return;
    }

    setProbLossLoading(true);
    try {
      const updatedUser = await adminApiService.updateUserProbLossConfig(
        userData.walletAddress,
        probLossForm
      );

      setUserData(updatedUser);
      setEditingProbLoss(false);
      setUserRewardsError(null);
    } catch (error) {
      const errorMessage = error instanceof ApiError
        ? error.message
        : 'Failed to update probLoss configuration';
      setUserRewardsError(errorMessage);
    } finally {
      setProbLossLoading(false);
    }
  };

  /**
   * Validate probLoss form inputs
   */
  const validateProbLossForm = (config: ProbLossConfig): string | null => {
    const { ruleType, rowThreshold, multiplierThreshold, lossProbability, notes } = config;

    // Validate ruleType
    if (!['row_based', 'multiplier_based', 'both'].includes(ruleType)) {
      return 'Invalid rule type';
    }

    // Validate lossProbability
    if (typeof lossProbability !== 'number' || lossProbability < 0 || lossProbability > 1) {
      return 'Loss probability must be between 0 and 1';
    }

    // Validate rule-specific thresholds
    if (ruleType === 'row_based' || ruleType === 'both') {
      if (rowThreshold === undefined || rowThreshold === null) {
        return 'Row threshold is required for row-based rules';
      }
      if (!Number.isInteger(rowThreshold) || rowThreshold < 0) {
        return 'Row threshold must be a non-negative integer';
      }
    }

    if (ruleType === 'multiplier_based' || ruleType === 'both') {
      if (multiplierThreshold === undefined || multiplierThreshold === null) {
        return 'Multiplier threshold is required for multiplier-based rules';
      }
      if (multiplierThreshold <= 0) {
        return 'Multiplier threshold must be greater than 0';
      }
    }

    // Validate notes length
    if (notes && notes.length > 200) {
      return 'Notes cannot exceed 200 characters';
    }

    return null;
  };

  /**
   * Update form field
   */
  const updateProbLossField = (field: keyof ProbLossConfig, value: any) => {
    setProbLossForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Update batch probLoss form field
   */
  const updateBatchProbLossField = (field: keyof ProbLossConfig, value: any) => {
    setBatchProbLossForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Handle clicking on "Referred Users" card to open modal
   */
  const handleViewReferredUsers = async () => {
    if (!userRewardsData?.walletAddress) return;

    setShowReferredUsersModal(true);
    setReferredUsersLoading(true);
    setReferredUsersData(null);
    setSelectedUsers(new Set());
    setShowBatchProbLossForm(false);
    setBatchProbLossError(null);
    setBatchProbLossSuccess(null);

    try {
      const data = await adminApiService.getReferredUsers(userRewardsData.walletAddress);
      setReferredUsersData(data);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to fetch referred users';
      setBatchProbLossError(errorMessage);
    } finally {
      setReferredUsersLoading(false);
    }
  };

  /**
   * Close referred users modal
   */
  const handleCloseReferredUsersModal = () => {
    setShowReferredUsersModal(false);
    setReferredUsersData(null);
    setSelectedUsers(new Set());
    setShowBatchProbLossForm(false);
    setBatchProbLossError(null);
    setBatchProbLossSuccess(null);
    setBatchProbLossForm({
      ruleType: 'multiplier_based',
      multiplierThreshold: 15,
      lossProbability: 0.5,
      notes: '',
    });
  };

  /**
   * Toggle selection of a single user
   */
  const handleToggleUserSelection = (walletAddress: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(walletAddress)) {
        newSet.delete(walletAddress);
      } else {
        newSet.add(walletAddress);
      }
      return newSet;
    });
  };

  /**
   * Toggle select all users
   */
  const handleToggleSelectAll = () => {
    if (!referredUsersData?.referredUsers) return;

    if (selectedUsers.size === referredUsersData.referredUsers.length) {
      // Deselect all
      setSelectedUsers(new Set());
    } else {
      // Select all
      const allAddresses = referredUsersData.referredUsers.map(u => u.walletAddress);
      setSelectedUsers(new Set(allAddresses));
    }
  };

  /**
   * Handle batch ProbLoss update
   */
  const handleBatchProbLossUpdate = async () => {
    if (selectedUsers.size === 0) {
      setBatchProbLossError('Please select at least one user');
      return;
    }

    // Validate the form
    const validationError = validateProbLossForm(batchProbLossForm);
    if (validationError) {
      setBatchProbLossError(validationError);
      return;
    }

    setBatchProbLossLoading(true);
    setBatchProbLossError(null);
    setBatchProbLossSuccess(null);

    try {
      const walletAddresses = Array.from(selectedUsers);
      const result = await adminApiService.batchUpdateProbLossConfig(walletAddresses, batchProbLossForm);

      if (result.success) {
        setBatchProbLossSuccess(`Successfully updated ${result.updatedCount} users`);
        // Refresh the referred users data
        if (userRewardsData?.walletAddress) {
          const data = await adminApiService.getReferredUsers(userRewardsData.walletAddress);
          setReferredUsersData(data);
        }
        // Clear selection and hide form
        setSelectedUsers(new Set());
        setShowBatchProbLossForm(false);
      } else {
        setBatchProbLossError(result.message);
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to batch update probLoss configuration';
      setBatchProbLossError(errorMessage);
    } finally {
      setBatchProbLossLoading(false);
    }
  };

  const formatTimestamp = (timestamp: Date): string => {
    return timestamp.toLocaleString();
  };

  const formatToken = (weiAmount: string): string => {
    return ethers.formatEther(weiAmount);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDateAgo = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch {
      return 'Unknown';
    }
  };

  const getStatusIcon = (status: GameOperation['status']): string => {
    switch (status) {
      case 'pending': return '⏳';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '❓';
    }
  };

  const getStatusColor = (status: GameOperation['status']): string => {
    switch (status) {
      case 'pending': return styles.statusPending;
      case 'success': return styles.statusSuccess;
      case 'error': return styles.statusError;
      default: return '';
    }
  };

  return (
    <div className={styles.gameManagement}>
      <div className={styles.managementHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <h2>🎮 Game Management</h2>
            <p className={styles.headerSubtitle}>Administrative tools for game operations and troubleshooting</p>
          </div>
          {operations.length > 0 && (
            <button 
              onClick={clearOperations}
              className={`btn ${styles.btnSecondary} ${styles.btnSmall}`}
              title="Clear operation history"
            >
              🗑️ Clear History
            </button>
          )}
        </div>
      </div>

      {/* Main Operations Grid */}
      <div className={styles.operationsGrid}>

        {/* User Management Section */}
        <div className={`${styles.managementSection} ${styles.userManagement}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon}>👤</div>
            <div className={styles.sectionTitle}>
              <h3>User Management</h3>
              <p className={styles.sectionDescription}>
                Query user data
              </p>
            </div>
          </div>

          <div className={styles.userManagementForm}>
            <div className={styles.formGroup}>
              <label htmlFor="search-input" className={styles.formLabel}>
                <span className={styles.labelText}>Search User <span className={styles.labelRequired}>*</span></span>
                <span className={styles.labelHint}>Enter wallet address or mark</span>
              </label>
              <div className={styles.inputWrapper}>
                <input
                  id="search-input"
                  type="text"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder="Enter wallet address (0x1234...abcd) or mark"
                  className={`${styles.walletAddressInput} ${!isValidWalletAddress(walletAddress) && walletAddress && walletAddress.length >= 1 ? styles.warning : ''}`}
                  disabled={userRewardsLoading || userDataLoading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !userRewardsLoading && !userDataLoading && isValidSearchInput(walletAddress)) {
                      handleSearchUserRewards();
                    }
                  }}
                />
                <div className={styles.inputStatus}>
                  {walletAddress && (
                    <span className={`${styles.statusIndicator} ${
                      isValidWalletAddress(walletAddress) 
                        ? styles.valid 
                        : walletAddress.length >= 1 
                          ? styles.warning 
                          : styles.invalid
                    }`}>
                      {isValidWalletAddress(walletAddress) ? '✓ Address' : walletAddress.length >= 1 ? '🏷️ Mark' : '✗'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.userManagementActions}>
              <button
                onClick={handleSearchUserRewards}
                disabled={userRewardsLoading || !isValidSearchInput(walletAddress)}
                className={`btn ${userRewardsLoading ? styles.btnLoading : styles.btnPrimary} ${styles.btnLarge}`}
              >
                <span className={styles.btnIcon}>{userRewardsLoading ? '⏳' : '🔍'}</span>
                <span className={styles.btnText}>
                  {userRewardsLoading ? 'Searching...' : 'Search User'}
                </span>
              </button>
              
              {(userRewardsData || userRewardsError) && (
                <button
                  onClick={clearUserRewardsData}
                  className={`btn ${styles.btnSecondary} ${styles.btnLarge}`}
                >
                  <span className={styles.btnIcon}>🗑️</span>
                  <span className={styles.btnText}>Clear</span>
                </button>
              )}
            </div>

            {userRewardsError && (
              <div className={`${styles.userManagementMessage} ${styles.error}`}>
                <span className={styles.messageIcon}>❌</span>
                <span className={styles.messageText}>{userRewardsError}</span>
              </div>
            )}

            {searchResults.length > 0 && (
              <div className={styles.multipleSearchResults}>
                <div className={styles.searchResultsHeader}>
                  <h3>Found {searchResults.length} users with that mark:</h3>
                </div>
                <div className={styles.searchResultsList}>
                  {searchResults.map((user, index) => (
                    <div key={user.walletAddress} className={styles.searchResultItem}>
                      <div className={styles.searchResultHeader}>
                        <span className={styles.searchResultIndex}>#{index + 1}</span>
                        <code className={styles.searchResultAddress}>{user.walletAddress}</code>
                        {user.mark && (
                          <span className={`${styles.statusBadge} ${styles.mark}`}>
                            🏷️ {user.mark}
                          </span>
                        )}
                      </div>
                      <div className={styles.searchResultDetails}>
                        {user.referrerCode && (
                          <span className={styles.searchResultInfo}>
                            Referred by: <code>{user.referrerCode}</code>
                          </span>
                        )}
                        {user.sourceData?.utm_source && (
                          <span className={styles.searchResultInfo}>
                            Source: {user.sourceData.utm_source}
                          </span>
                        )}
                        <span className={styles.searchResultInfo}>
                          Created: {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                        {user.totalBnbBet && (
                          <span className={styles.searchResultInfo}>
                            Total Bet: {formatToken(user.totalBnbBet)} BNB
                          </span>
                        )}
                        {user.totalBnbPayout && (
                          <span className={styles.searchResultInfo}>
                            Total Payout: {formatToken(user.totalBnbPayout)} BNB
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(userRewardsData || userData) && (
              <div className={styles.userRewardsData}>
                <div className={styles.userRewardsHeader}>
                  <div className={styles.userAddressDisplay}>
                    <span className={styles.userAddressLabel}>User:</span>
                    <code className={styles.userAddressCode}>
                      {userRewardsData?.walletAddress || userData?.walletAddress}
                    </code>
                    <div className={styles.markSection}>
                      {editingMark ? (
                        <div className={styles.markEditForm}>
                          <input
                            type="text"
                            value={markInput}
                            onChange={(e) => setMarkInput(e.target.value)}
                            placeholder="Enter mark..."
                            maxLength={50}
                            className={styles.markInput}
                            disabled={markLoading}
                          />
                          <button
                            onClick={handleSaveMark}
                            disabled={markLoading}
                            className={styles.markSaveButton}
                          >
                            {markLoading ? '⏳' : '✓'}
                          </button>
                          <button
                            onClick={handleCancelEditMark}
                            disabled={markLoading}
                            className={styles.markCancelButton}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className={styles.markDisplay}>
                          {userData?.mark && (
                            <span className={`${styles.statusBadge} ${styles.mark}`}>
                              🏷️ {userData.mark}
                            </span>
                          )}
                          <button
                            onClick={handleEditMark}
                            className={styles.markEditButton}
                            title={userData?.mark ? 'Edit mark' : 'Add mark'}
                          >
                            {userData?.mark ? '✏️' : '🏷️+'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.userStatus}>
                    {userData?.isAdmin && (
                      <span className={`${styles.statusBadge} ${styles.admin}`}>
                        🔑 Admin
                      </span>
                    )}
                    {userRewardsData?.isVIP && (
                      <span className={`${styles.statusBadge} ${styles.vip}`}>
                        👑 VIP
                      </span>
                    )}
                    <span className={`${styles.statusBadge} ${styles.ratio}`}>
                      {userRewardsData?.referralRatio || userData?.referralRatio || 2}% Ratio
                      {userData?.referralOnlyLost && <span> (Only Lost)</span>}
                    </span>
                    {/* Claim Blocked Badge */}
                    {userData?.claimBlocked && (
                      <span className={`${styles.statusBadge} ${styles.claimBlocked}`}>
                        ⛔ Claims Blocked
                      </span>
                    )}
                    {!userData?.claimBlocked && (
                      <span className={`${styles.statusBadge} ${styles.claimAllowed}`}>
                        ✅ Claims Allowed
                      </span>
                    )}
                    <button
                      onClick={handleToggleClaimBlocked}
                      disabled={claimBlockedLoading}
                      className={styles.toggleButton}
                      title={userData?.claimBlocked ? "Unblock claims" : "Block claims"}
                    >
                      {claimBlockedLoading ? (
                        <span className={styles.loadingSpinner}>⏳</span>
                      ) : userData?.claimBlocked ? (
                        '🔓 Unblock'
                      ) : (
                        '🔒 Block'
                      )}
                    </button>
                  </div>
                </div>

                {/* User Profile Section */}
                {userData && (
                  <div className={styles.userProfileSection}>
                    <div className={styles.sectionTitle}>
                      <span className={styles.sectionIcon}>👤</span>
                      <span>User Profile</span>
                    </div>
                    <div className={styles.profileGrid}>
                      <div className={styles.profileCard}>
                        <div className={styles.profileCardHeader}>
                          <span className={styles.profileCardIcon}>📅</span>
                          <span className={styles.profileCardTitle}>Registration</span>
                        </div>
                        <div className={styles.profileCardValue}>
                          <span className={styles.profileDate}>{formatDate(userData.createdAt)}</span>
                          <span className={styles.profileDateAgo}>{formatDateAgo(userData.createdAt)}</span>
                        </div>
                      </div>

                      <div className={styles.profileCard}>
                        <div className={styles.profileCardHeader}>
                          <span className={styles.profileCardIcon}>🎰</span>
                          <span className={styles.profileCardTitle}>Games Played</span>
                        </div>
                        <div className={styles.profileCardValue}>
                          <span className={styles.profileNumber}>{userData.totalGamesPlayed || 0}</span>
                          <span className={styles.profileLabel}>total games</span>
                        </div>
                      </div>

                      <div className={styles.profileCard}>
                        <div className={styles.profileCardHeader}>
                          <span className={styles.profileCardIcon}>🎯</span>
                          <span className={styles.profileCardTitle}>Last Game</span>
                        </div>
                        <div className={styles.profileCardValue}>
                          {userData.lastGameDate ? (
                            <>
                              <span className={styles.profileDate}>{userData.lastGameDate}</span>
                              <span className={styles.profileDateAgo}>{formatDateAgo(userData.lastGameDate)}</span>
                            </>
                          ) : (
                            <span className={styles.profileNoData}>No games played</span>
                          )}
                        </div>
                      </div>

                      <div className={styles.profileCard}>
                        <div className={styles.profileCardHeader}>
                          <span className={styles.profileCardIcon}>🔥</span>
                          <span className={styles.profileCardTitle}>Current Streak</span>
                        </div>
                        <div className={styles.profileCardValue}>
                          <span className={`${styles.profileNumber} ${(userData.currentStreak || 0) > 0 ? styles.streakActive : ''}`}>
                            {userData.currentStreak || 0}
                          </span>
                          <span className={styles.profileLabel}>
                            best: {userData.bestStreak || 0}
                          </span>
                        </div>
                      </div>

                      <div className={styles.profileCard}>
                        <div className={styles.profileCardHeader}>
                          <span className={styles.profileCardIcon}>💎</span>
                          <span className={styles.profileCardTitle}>Total Bet Amount</span>
                        </div>
                        <div className={styles.profileCardValue}>
                          <span className={styles.profileBnb}>
                            {formatToken(userData.totalBnbBet || '0')} BNB
                          </span>
                          {/* <span className={styles.profileWei}>{userData.totalBnbBet || '0'} wei</span> */}
                        </div>
                      </div>

                      <div className={styles.profileCard}>
                        <div className={styles.profileCardHeader}>
                          <span className={styles.profileCardIcon}>💰</span>
                          <span className={styles.profileCardTitle}>Total Payout Amount</span>
                        </div>
                        <div className={styles.profileCardValue}>
                          <span className={styles.profileBnb}>
                            {formatToken(userData.totalBnbPayout || '0')} BNB
                          </span>
                          {/* <span className={styles.profileWei}>{userData.totalBnbPayout || '0'} wei</span> */}
                        </div>
                      </div>

                      <div className={styles.profileCard}>
                        <div className={styles.profileCardHeader}>
                          <span className={styles.profileCardIcon}>🪙</span>
                          <span className={styles.profileCardTitle}>DXF Mined</span>
                        </div>
                        <div className={styles.profileCardValue}>
                          <span className={styles.profileBnb}>
                            {formatToken(userData.totalDxfMined || '0')} DXF
                          </span>
                          {/* <span className={styles.profileWei}>{userData.totalDxfMined || '0'} wei</span> */}
                        </div>
                      </div>

                    </div>
                  </div>
                )}

                {/* Referral Information */}
                {userData && (
                  <div className={styles.referralInfoSection}>
                    <div className={styles.sectionTitle}>
                      <span className={styles.sectionIcon}>🔗</span>
                      <span>Referral Information</span>
                    </div>
                    <div className={styles.referralInfo}>
                      <div className={styles.referralItem}>
                        <span className={styles.referralLabel}>Referrer:</span>
                        <span className={styles.referralValue}>
                          {userData.referrerCode ? (
                            <code className={styles.referralCode}>{userData.referrerCode}</code>
                          ) : (
                            <span className={styles.organic}>Organic User</span>
                          )}
                        </span>
                      </div>
                      {userData.sourceData && (
                        <div className={styles.referralItem}>
                          <span className={styles.referralLabel}>Source:</span>
                          <span className={styles.referralValue}>
                            {userData.sourceData.utm_source && (
                              <span className={styles.sourceTag}>{userData.sourceData.utm_source}</span>
                            )}
                            {userData.sourceData.utm_medium && (
                              <span className={styles.sourceTag}>{userData.sourceData.utm_medium}</span>
                            )}
                            {userData.sourceData.utm_campaign && (
                              <span className={styles.sourceTag}>{userData.sourceData.utm_campaign}</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Referral Rewards */}
                {userRewardsData && (
                  <div className={styles.rewardsSection}>
                    <div className={styles.sectionTitle}>
                      <span className={styles.sectionIcon}>🎁</span>
                      <span>Referral Rewards</span>
                    </div>
                    <div className={styles.rewardsGrid}>
                      <div className={styles.rewardCard}>
                        <div className={styles.rewardCardHeader}>
                          <span className={styles.rewardCardIcon}>💰</span>
                          <span className={styles.rewardCardTitle}>Total Rewards</span>
                        </div>
                        <div className={styles.rewardCardValue}>
                          <span className={styles.rewardBnb}>{userRewardsData.totalRewardsBNB} BNB</span>
                          {/* <span className={styles.rewardWei}>{userRewardsData.totalRewards} wei</span> */}
                        </div>
                      </div>

                      <div className={styles.rewardCard}>
                        <div className={styles.rewardCardHeader}>
                          <span className={styles.rewardCardIcon}>✅</span>
                          <span className={styles.rewardCardTitle}>Paid Rewards</span>
                        </div>
                        <div className={styles.rewardCardValue}>
                          <span className={styles.rewardBnb}>{userRewardsData.paidRewardsBNB} BNB</span>
                          {/* <span className={styles.rewardWei}>{userRewardsData.paidRewards} wei</span> */}
                        </div>
                      </div>

                      <div className={styles.rewardCard}>
                        <div className={styles.rewardCardHeader}>
                          <span className={styles.rewardCardIcon}>⏳</span>
                          <span className={styles.rewardCardTitle}>Unpaid Rewards</span>
                        </div>
                        <div className={styles.rewardCardValue}>
                          <span className={styles.rewardBnb}>{userRewardsData.unpaidRewardsBNB} BNB</span>
                          {/* <span className={styles.rewardWei}>{userRewardsData.unpaidRewards} wei</span> */}
                          {userRewardsData.canClaim && (
                            <span className={`${styles.claimStatus} ${styles.canClaim}`}>
                              🎯 Can Claim
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        className={`${styles.rewardCard} ${userRewardsData.referredUsers > 0 ? styles.clickable : ''}`}
                        onClick={userRewardsData.referredUsers > 0 ? handleViewReferredUsers : undefined}
                        style={userRewardsData.referredUsers > 0 ? { cursor: 'pointer' } : undefined}
                        title={userRewardsData.referredUsers > 0 ? 'Click to view referred users' : undefined}
                      >
                        <div className={styles.rewardCardHeader}>
                          <span className={styles.rewardCardIcon}>👥</span>
                          <span className={styles.rewardCardTitle}>Referred Users</span>
                        </div>
                        <div className={styles.rewardCardValue}>
                          <span className={styles.userCount}>{userRewardsData.referredUsers}</span>
                          <span className={styles.userCountLabel}>users</span>
                          {userRewardsData.referredUsers > 0 && (
                            <span className={styles.viewMoreHint}>Click to view</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ProbLoss Configuration Section */}
                {userData && (
                  <div className={styles.rewardsSection}>
                    <div className={styles.sectionTitle}>
                      <span className={styles.sectionIcon}>🎲</span>
                      <span>ProbLoss Configuration</span>
                    </div>

                    {!editingProbLoss ? (
                      /* Display Mode */
                      <div className={styles.probLossDisplay}>
                        {userData.probLossConfig ? (
                          <>
                            <div className={styles.probLossInfo}>
                              <div className={styles.probLossItem}>
                                <span className={styles.probLossLabel}>Rule Type:</span>
                                <span className={`${styles.statusBadge} ${styles.ruleType}`}>
                                  {userData.probLossConfig.ruleType === 'row_based' && '📊 Row Based'}
                                  {userData.probLossConfig.ruleType === 'multiplier_based' && '📈 Multiplier Based'}
                                  {userData.probLossConfig.ruleType === 'both' && '🎯 Both Conditions'}
                                </span>
                              </div>

                              {(userData.probLossConfig.ruleType === 'row_based' || userData.probLossConfig.ruleType === 'both') && (
                                <div className={styles.probLossItem}>
                                  <span className={styles.probLossLabel}>Row Threshold:</span>
                                  <span className={styles.probLossValue}>≥ {userData.probLossConfig.rowThreshold}</span>
                                </div>
                              )}

                              {(userData.probLossConfig.ruleType === 'multiplier_based' || userData.probLossConfig.ruleType === 'both') && (
                                <div className={styles.probLossItem}>
                                  <span className={styles.probLossLabel}>Multiplier Threshold:</span>
                                  <span className={styles.probLossValue}>&gt; {userData.probLossConfig.multiplierThreshold}x</span>
                                </div>
                              )}

                              <div className={styles.probLossItem}>
                                <span className={styles.probLossLabel}>Loss Probability:</span>
                                <span className={`${styles.probLossValue} ${styles.probability}`}>
                                  {(userData.probLossConfig.lossProbability * 100).toFixed(1)}%
                                </span>
                              </div>

                              {userData.probLossConfig.notes && (
                                <div className={styles.probLossItem}>
                                  <span className={styles.probLossLabel}>Notes:</span>
                                  <span className={styles.probLossNotes}>{userData.probLossConfig.notes}</span>
                                </div>
                              )}
                            </div>

                            <button
                              onClick={handleEditProbLoss}
                              className={`btn ${styles.btnSecondary} ${styles.btnSmall}`}
                              disabled={probLossLoading}
                            >
                              <span className={styles.btnIcon}>✏️</span>
                              <span className={styles.btnText}>Edit Config</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <div className={styles.probLossNoConfig}>
                              <span className={styles.noConfigIcon}>⚙️</span>
                              <span className={styles.noConfigText}>No custom configuration</span>
                              <span className={styles.noConfigHint}>Using default rules</span>
                            </div>

                            <button
                              onClick={handleEditProbLoss}
                              className={`btn ${styles.btnPrimary} ${styles.btnSmall}`}
                              disabled={probLossLoading}
                            >
                              <span className={styles.btnIcon}>➕</span>
                              <span className={styles.btnText}>Add Config</span>
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      /* Edit Mode */
                      <div className={styles.probLossEditForm}>
                        {/* Rule Type Selection */}
                        <div className={styles.formGroup}>
                          <div className={styles.formLabel}>
                            <span className={styles.labelText}>Rule Type <span className={styles.labelRequired}>*</span></span>
                          </div>
                          <div className={styles.radioGroup}>
                            <label className={styles.radioLabel}>
                              <div className={styles.radioLabelMain}>
                                <input
                                  type="radio"
                                  value="row_based"
                                  checked={probLossForm.ruleType === 'row_based'}
                                  onChange={(e) => updateProbLossField('ruleType', e.target.value)}
                                  disabled={probLossLoading}
                                />
                                <span className={styles.radioLabelText}>Row Based</span>
                              </div>
                              <div className={styles.radioHint}>Apply when row ≥ threshold</div>
                            </label>

                            <label className={styles.radioLabel}>
                              <div className={styles.radioLabelMain}>
                                <input
                                  type="radio"
                                  value="multiplier_based"
                                  checked={probLossForm.ruleType === 'multiplier_based'}
                                  onChange={(e) => updateProbLossField('ruleType', e.target.value)}
                                  disabled={probLossLoading}
                                />
                                <span className={styles.radioLabelText}>Multiplier Based</span>
                              </div>
                              <div className={styles.radioHint}>Apply when multiplier &gt; threshold</div>
                            </label>

                            <label className={styles.radioLabel}>
                              <div className={styles.radioLabelMain}>
                                <input
                                  type="radio"
                                  value="both"
                                  checked={probLossForm.ruleType === 'both'}
                                  onChange={(e) => updateProbLossField('ruleType', e.target.value)}
                                  disabled={probLossLoading}
                                />
                                <span className={styles.radioLabelText}>Both Conditions</span>
                              </div>
                              <div className={styles.radioHint}>Both conditions must be met</div>
                            </label>
                          </div>
                        </div>

                        {/* Row Threshold (for row_based or both) */}
                        {(probLossForm.ruleType === 'row_based' || probLossForm.ruleType === 'both') && (
                          <div className={styles.formGroup}>
                            <div className={styles.formLabel}>
                              <span className={styles.labelText}>Row Threshold <span className={styles.labelRequired}>*</span></span>
                              <span className={styles.labelHint}>Apply probLoss when row ≥ this value</span>
                            </div>
                            <input
                              type="number"
                              value={probLossForm.rowThreshold ?? ''}
                              onChange={(e) => updateProbLossField('rowThreshold', parseInt(e.target.value) || 0)}
                              onWheel={(e) => e.currentTarget.blur()}
                              min="0"
                              step="1"
                              placeholder="e.g., 3"
                              className={styles.input}
                              disabled={probLossLoading}
                            />
                          </div>
                        )}

                        {/* Multiplier Threshold (for multiplier_based or both) */}
                        {(probLossForm.ruleType === 'multiplier_based' || probLossForm.ruleType === 'both') && (
                          <div className={styles.formGroup}>
                            <div className={styles.formLabel}>
                              <span className={styles.labelText}>Multiplier Threshold <span className={styles.labelRequired}>*</span></span>
                              <span className={styles.labelHint}>Apply probLoss when multiplier &gt; this value</span>
                            </div>
                            <input
                              type="number"
                              value={probLossForm.multiplierThreshold ?? ''}
                              onChange={(e) => updateProbLossField('multiplierThreshold', parseFloat(e.target.value) || 0)}
                              onWheel={(e) => e.currentTarget.blur()}
                              min="0"
                              step="0.1"
                              placeholder="e.g., 15.0"
                              className={styles.input}
                              disabled={probLossLoading}
                            />
                          </div>
                        )}

                        {/* Loss Probability */}
                        <div className={styles.formGroup}>
                          <div className={styles.formLabel}>
                            <span className={styles.labelText}>Loss Probability <span className={styles.labelRequired}>*</span></span>
                            <span className={styles.labelHint}>
                              Chance of loss (0.0-1.0, e.g., 0.5 = 50%)
                            </span>
                          </div>
                          <input
                            type="number"
                            value={probLossForm.lossProbability}
                            onChange={(e) => updateProbLossField('lossProbability', parseFloat(e.target.value) || 0)}
                            onWheel={(e) => e.currentTarget.blur()}
                            min="0"
                            max="1"
                            step="0.01"
                            placeholder="e.g., 0.6"
                            className={styles.input}
                            disabled={probLossLoading}
                          />
                          <div className={styles.probabilityDisplay}>
                            {(probLossForm.lossProbability * 100).toFixed(1)}% chance
                          </div>
                        </div>

                        {/* Notes */}
                        <div className={styles.formGroup}>
                          <div className={styles.formLabel}>
                            <span className={styles.labelText}>Notes (Optional)</span>
                            <span className={styles.labelHint}>Max 200 characters</span>
                          </div>
                          <textarea
                            value={probLossForm.notes || ''}
                            onChange={(e) => updateProbLossField('notes', e.target.value)}
                            placeholder="Add notes about this configuration..."
                            maxLength={200}
                            rows={3}
                            className={styles.textarea}
                            disabled={probLossLoading}
                          />
                          <div className={styles.charCount}>
                            {(probLossForm.notes || '').length}/200
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className={styles.formActions}>
                          <button
                            onClick={handleSaveProbLoss}
                            disabled={probLossLoading}
                            className={`btn ${probLossLoading ? styles.btnLoading : styles.btnPrimary}`}
                          >
                            <span className={styles.btnIcon}>{probLossLoading ? '⏳' : '💾'}</span>
                            <span className={styles.btnText}>{probLossLoading ? 'Saving...' : 'Save Config'}</span>
                          </button>

                          <button
                            onClick={handleCancelProbLoss}
                            disabled={probLossLoading}
                            className={`btn ${styles.btnSecondary}`}
                          >
                            <span className={styles.btnIcon}>❌</span>
                            <span className={styles.btnText}>Cancel</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Resend Death Tile Section */}
        <div className={`${styles.managementSection} ${styles.primaryOperation}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon}>💀</div>
            <div className={styles.sectionTitle}>
              <h3>Resend Death Tile Message</h3>
              <p className={styles.sectionDescription}>
                Resend death tile messages to the queue for games that may have failed processing
              </p>
            </div>
          </div>

          <div className={styles.operationForm}>
            <div className={styles.formGroup}>
              <label htmlFor="game-id-input" className={styles.formLabel}>
                <span className={styles.labelText}>Game ID <span className={styles.labelRequired}>*</span></span>
              </label>
              <div className={styles.inputWrapper}>
                <input
                  id="game-id-input"
                  type="text"
                  value={gameId}
                  onChange={(e) => setGameId(e.target.value)}
                  placeholder="Enter game ID (e.g., 507f1f77bcf86cd799439011)"
                  className={`${styles.gameIdInput} ${!isValidGameId(gameId) && gameId ? styles.invalid : ''}`}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !loading && isValidGameId(gameId)) {
                      handleResendDeathTile();
                    }
                  }}
                />
                <div className={styles.inputStatus}>
                  {gameId && (
                    <span className={`${styles.statusIndicator} ${isValidGameId(gameId) ? styles.valid : styles.invalid}`}>
                      {isValidGameId(gameId) ? '✓' : '✗'}
                    </span>
                  )}
                </div>
              </div>
              {gameId && !isValidGameId(gameId) && (
                <span className={styles.formError}>Please enter a valid game ID</span>
              )}
            </div>

            <button
              onClick={handleResendDeathTile}
              disabled={loading || !isValidGameId(gameId)}
              className={`btn ${loading ? styles.btnLoading : styles.btnPrimary} ${styles.btnLarge} ${styles.operationBtn}`}
            >
              <span className={styles.btnIcon}>{loading ? '⏳' : '🔄'}</span>
              <span className={styles.btnText}>
                {loading ? 'Processing...' : 'Resend Death Tile'}
              </span>
            </button>

            {/* Operation Tips */}
            <div className={styles.operationTips}>
              <div className={styles.tipsHeader}>
                <span className={styles.tipsIcon}>💡</span>
                <span className={styles.tipsTitle}>Tips & Guidelines</span>
              </div>
              <div className={styles.tipsContent}>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}>🎯</div>
                  <div className={styles.tipText}>
                    <strong>Target Games:</strong> Only works for games with "lost" status
                  </div>
                </div>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}>🔄</div>
                  <div className={styles.tipText}>
                    <strong>Process:</strong> Reconstructs and resends death tile message to queue
                  </div>
                </div>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}>⚠️</div>
                  <div className={styles.tipText}>
                    <strong>Use Case:</strong> When death tile processing has failed or stalled
                  </div>
                </div>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}>✅</div>
                  <div className={styles.tipText}>
                    <strong>Verification:</strong> Check game status before using this function
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scheduled Jobs Controls */}
        <div className={`${styles.managementSection} ${styles.jobControls}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon}>⚡</div>
            <div className={styles.sectionTitle}>
              <h3>Scheduled Jobs</h3>
              <p className={styles.sectionDescription}>
                Manually trigger daily maintenance jobs and system operations
              </p>
            </div>
          </div>

          <div className={styles.jobControlsGrid}>
            {/* Draw Completion */}
            <div className={styles.jobControlCard}>
              <div className={styles.jobHeader}>
                <div className={styles.jobIcon}>🎲</div>
                <div className={styles.jobInfo}>
                  <h4>Daily Draw Completion</h4>
                  <p>Complete previous day's draw and select winners</p>
                  <span className={styles.jobSchedule}>Scheduled: Daily at 00:30 UTC</span>
                </div>
              </div>
              <div className={styles.jobActions}>
                {drawCompletionError && (
                  <div className={`${styles.jobMessage} ${styles.error}`}>
                    <span className={styles.messageIcon}>❌</span>
                    <span className={styles.messageText}>{drawCompletionError}</span>
                    <button 
                      onClick={clearDrawCompletionError}
                      className={styles.messageClose}
                      title="Clear error"
                    >
                      ×
                    </button>
                  </div>
                )}
                
                {drawCompletionSuccess && (
                  <div className={`${styles.jobMessage} ${styles.success}`}>
                    <span className={styles.messageIcon}>✅</span>
                    <span className={styles.messageText}>{drawCompletionSuccess}</span>
                    <button 
                      onClick={clearDrawCompletionSuccess}
                      className={styles.messageClose}
                      title="Clear message"
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className={styles.drawDateForm}>
                  <label htmlFor="draw-date-input" className={styles.formLabel}>
                    <span className={styles.labelText}>Date (Optional)</span>
                    <span className={styles.labelHint}>Leave empty for previous day</span>
                  </label>
                  <div className={styles.inputWrapper}>
                    <input
                      id="draw-date-input"
                      type="date"
                      value={drawDate}
                      onChange={(e) => setDrawDate(e.target.value)}
                      max={new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]} // Yesterday
                      className={`${styles.dateInput} ${drawDate && !isValidDrawDate(drawDate) ? styles.invalid : ''}`}
                      disabled={drawCompletionLoading}
                    />
                    <div className={styles.inputStatus}>
                      {drawDate && (
                        <span className={`${styles.statusIndicator} ${isValidDrawDate(drawDate) ? styles.valid : styles.invalid}`}>
                          {isValidDrawDate(drawDate) ? '✓' : '✗'}
                        </span>
                      )}
                    </div>
                  </div>
                  {drawDate && !isValidDrawDate(drawDate) && (
                    <span className={styles.formError}>Date must be in the past (YYYY-MM-DD format)</span>
                  )}
                </div>
                
                <button
                  onClick={handleDrawCompletion}
                  disabled={drawCompletionLoading || (!!drawDate && !isValidDrawDate(drawDate))}
                  className={`btn ${drawCompletionLoading ? styles.btnLoading : styles.btnPrimary} ${styles.jobTriggerBtn}`}
                >
                  <span className={styles.btnIcon}>{drawCompletionLoading ? '⏳' : '🎲'}</span>
                  <span className={styles.btnText}>
                    {drawCompletionLoading ? 'Completing Draw...' : 'Complete Draw'}
                  </span>
                </button>
              </div>
            </div>

            {/* Streak Reset */}
            <div className={styles.jobControlCard}>
              <div className={styles.jobHeader}>
                <div className={styles.jobIcon}>🔄</div>
                <div className={styles.jobInfo}>
                  <h4>User Streak Reset</h4>
                  <p>Reset streaks for users who didn't play yesterday</p>
                  <span className={styles.jobSchedule}>Scheduled: Daily at 00:05 UTC</span>
                </div>
              </div>
              <div className={styles.jobActions}>
                {streakResetError && (
                  <div className={`${styles.jobMessage} ${styles.error}`}>
                    <span className={styles.messageIcon}>❌</span>
                    <span className={styles.messageText}>{streakResetError}</span>
                    <button 
                      onClick={clearStreakResetError}
                      className={styles.messageClose}
                      title="Clear error"
                    >
                      ×
                    </button>
                  </div>
                )}
                
                {streakResetSuccess && (
                  <div className={`${styles.jobMessage} ${styles.success}`}>
                    <span className={styles.messageIcon}>✅</span>
                    <span className={styles.messageText}>{streakResetSuccess}</span>
                    <button 
                      onClick={clearStreakResetSuccess}
                      className={styles.messageClose}
                      title="Clear message"
                    >
                      ×
                    </button>
                  </div>
                )}
                
                <button
                  onClick={handleStreakReset}
                  disabled={streakResetLoading}
                  className={`btn ${streakResetLoading ? styles.btnLoading : styles.btnDanger} ${styles.jobTriggerBtn}`}
                >
                  <span className={styles.btnIcon}>{streakResetLoading ? '⏳' : '🔄'}</span>
                  <span className={styles.btnText}>
                    {streakResetLoading ? 'Resetting Streaks...' : 'Reset Stale Streaks'}
                  </span>
                </button>
              </div>
            </div>

            {/* Leaderboard Snapshot */}
            <div className={styles.jobControlCard}>
              <div className={styles.jobHeader}>
                <div className={styles.jobIcon}>🏆</div>
                <div className={styles.jobInfo}>
                  <h4>Weekly Leaderboard Snapshot</h4>
                  <p>Generate snapshot and trigger reward calculation</p>
                  <span className={styles.jobSchedule}>Scheduled: Weekly on Monday at 00:15 UTC</span>
                </div>
              </div>
              <div className={styles.jobActions}>
                {leaderboardSnapshotError && (
                  <div className={`${styles.jobMessage} ${styles.error}`}>
                    <span className={styles.messageIcon}>❌</span>
                    <span className={styles.messageText}>{leaderboardSnapshotError}</span>
                    <button 
                      onClick={clearLeaderboardSnapshotError}
                      className={styles.messageClose}
                      title="Clear error"
                    >
                      ×
                    </button>
                  </div>
                )}
                
                {leaderboardSnapshotSuccess && (
                  <div className={`${styles.jobMessage} ${styles.success}`}>
                    <span className={styles.messageIcon}>✅</span>
                    <span className={styles.messageText}>{leaderboardSnapshotSuccess}</span>
                    <button 
                      onClick={clearLeaderboardSnapshotSuccess}
                      className={styles.messageClose}
                      title="Clear message"
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className={styles.leaderboardWeekForm}>
                  <label htmlFor="leaderboard-week-input" className={styles.formLabel}>
                    <span className={styles.labelText}>Week Start Date (Optional)</span>
                    <span className={styles.labelHint}>Leave empty for current week (must be Monday)</span>
                  </label>
                  <div className={styles.inputWrapper}>
                    <input
                      id="leaderboard-week-input"
                      type="date"
                      value={leaderboardWeek}
                      onChange={(e) => setLeaderboardWeek(e.target.value)}
                      className={`${styles.dateInput} ${leaderboardWeek && !isValidLeaderboardWeek(leaderboardWeek) ? styles.invalid : ''}`}
                      disabled={leaderboardSnapshotLoading}
                    />
                    <div className={styles.inputStatus}>
                      {leaderboardWeek && (
                        <span className={`${styles.statusIndicator} ${isValidLeaderboardWeek(leaderboardWeek) ? styles.valid : styles.invalid}`}>
                          {isValidLeaderboardWeek(leaderboardWeek) ? '✓' : '✗'}
                        </span>
                      )}
                    </div>
                  </div>
                  {leaderboardWeek && !isValidLeaderboardWeek(leaderboardWeek) && (
                    <span className={styles.formError}>Date must be a Monday (YYYY-MM-DD format)</span>
                  )}
                </div>
                
                <button
                  onClick={handleLeaderboardSnapshot}
                  disabled={leaderboardSnapshotLoading || (!!leaderboardWeek && !isValidLeaderboardWeek(leaderboardWeek))}
                  className={`btn ${leaderboardSnapshotLoading ? styles.btnLoading : styles.btnPrimary} ${styles.jobTriggerBtn}`}
                >
                  <span className={styles.btnIcon}>{leaderboardSnapshotLoading ? '⏳' : '🏆'}</span>
                  <span className={styles.btnText}>
                    {leaderboardSnapshotLoading ? 'Generating Snapshot...' : 'Generate Snapshot'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>



      </div>

      {/* Operation History */}
      {operations.length > 0 && (
        <div className={`${styles.managementSection} ${styles.operationHistory}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon}>📋</div>
            <div className={styles.sectionTitle}>
              <h3>Recent Operations</h3>
              <p className={styles.sectionDescription}>History of your latest game management operations</p>
            </div>
            <div className={styles.operationsCounter}>
              <span className={styles.counterLabel}>Total:</span>
              <span className={styles.counterValue}>{operations.length}</span>
            </div>
          </div>
          
          <div className={styles.operationsTimeline}>
            {operations.map((operation, index) => (
              <div key={operation.id} className={`operation-item ${getStatusColor(operation.status)}`}>
                <div className={styles.operationTimelineMarker}>
                  <div className={styles.timelineDot}></div>
                  {index < operations.length - 1 && <div className={styles.timelineLine}></div>}
                </div>
                
                <div className={styles.operationContent}>
                  <div className={styles.operationHeader}>
                    <div className={styles.operationInfo}>
                      <div className={styles.operationStatus}>
                        <span className={styles.statusIcon}>{getStatusIcon(operation.status)}</span>
                        <span className={styles.statusText}>{operation.status.toUpperCase()}</span>
                      </div>
                      <div className={styles.operationDetailsText}>
                        <span className={styles.operationType}>Resend Death Tile</span>
                        <span className={styles.operationGameId}>
                          <span className={styles.gameIdLabel}>Game:</span>
                          <code className={styles.gameIdCode}>{operation.gameId}</code>
                        </span>
                      </div>
                    </div>
                    <div className={styles.operationTimestamp}>
                      <span className={styles.timestampLabel}>Executed</span>
                      <span className={styles.timestampValue}>{formatTimestamp(operation.timestamp)}</span>
                    </div>
                  </div>

                  {operation.status === 'success' && operation.result && (
                    <div className={`${styles.operationResult} ${styles.success}`}>
                      <div className={styles.resultMessage}>
                        <span className={styles.resultIcon}>✅</span>
                        <span className={styles.resultText}>{operation.result.message}</span>
                      </div>
                      {operation.result.queueResult && (
                        <details className={styles.operationDetails}>
                          <summary className={styles.detailsToggle}>
                            <span>View Technical Details</span>
                            <span className={styles.detailsArrow}>▼</span>
                          </summary>
                          <div className={styles.detailsContent}>
                            <pre className={styles.operationData}>
                              {JSON.stringify(operation.result, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}
                    </div>
                  )}

                  {operation.status === 'error' && operation.error && (
                    <div className={`${styles.operationResult} ${styles.error}`}>
                      <div className={styles.resultMessage}>
                        <span className={styles.resultIcon}>❌</span>
                        <span className={styles.resultText}>{operation.error}</span>
                      </div>
                    </div>
                  )}

                  {operation.status === 'pending' && (
                    <div className={`${styles.operationResult} ${styles.pending}`}>
                      <div className={styles.resultMessage}>
                        <span className={styles.resultIcon}>⏳</span>
                        <span className={styles.resultText}>Processing operation...</span>
                      </div>
                      <div className={styles.progressBar}>
                        <div className={styles.progressFill}></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Referred Users Modal - rendered via portal to escape stacking context */}
      {showReferredUsersModal && createPortal(
        <div className={styles.modalOverlay} onClick={handleCloseReferredUsersModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>👥 Referred Users</h3>
              <button
                onClick={handleCloseReferredUsersModal}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              {referredUsersLoading && (
                <div className={styles.modalLoading}>
                  <span className={styles.loadingSpinner}>⏳</span>
                  <span>Loading referred users...</span>
                </div>
              )}

              {batchProbLossError && (
                <div className={`${styles.userManagementMessage} ${styles.error}`}>
                  <span className={styles.messageIcon}>❌</span>
                  <span className={styles.messageText}>{batchProbLossError}</span>
                  <button
                    onClick={() => setBatchProbLossError(null)}
                    className={styles.messageClose}
                  >
                    ×
                  </button>
                </div>
              )}

              {batchProbLossSuccess && (
                <div className={`${styles.userManagementMessage} ${styles.success}`}>
                  <span className={styles.messageIcon}>✅</span>
                  <span className={styles.messageText}>{batchProbLossSuccess}</span>
                  <button
                    onClick={() => setBatchProbLossSuccess(null)}
                    className={styles.messageClose}
                  >
                    ×
                  </button>
                </div>
              )}

              {referredUsersData && (
                <>
                  {/* Stats bar */}
                  <div className={styles.modalStatsBar}>
                    <div className={styles.modalStat}>
                      <span className={styles.modalStatLabel}>Total Users:</span>
                      <span className={styles.modalStatValue}>{referredUsersData.totalCount}</span>
                    </div>
                    <div className={styles.modalStat}>
                      <span className={styles.modalStatLabel}>Referrer Code:</span>
                      <code className={styles.modalStatCode}>{referredUsersData.referrerCode}</code>
                    </div>
                  </div>

                  {/* Selection controls */}
                  {referredUsersData.referredUsers.length > 0 && (
                    <div className={styles.selectionControls}>
                      <label className={styles.selectAllCheckbox}>
                        <input
                          type="checkbox"
                          checked={selectedUsers.size === referredUsersData.referredUsers.length}
                          onChange={handleToggleSelectAll}
                        />
                        <span>Select All ({selectedUsers.size}/{referredUsersData.referredUsers.length})</span>
                      </label>

                      {selectedUsers.size > 0 && (
                        <button
                          onClick={() => setShowBatchProbLossForm(!showBatchProbLossForm)}
                          className={`btn ${styles.btnSecondary} ${styles.btnSmall}`}
                        >
                          <span className={styles.btnIcon}>🎲</span>
                          <span className={styles.btnText}>
                            {showBatchProbLossForm ? 'Hide' : 'Batch ProbLoss Config'}
                          </span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Batch ProbLoss Form */}
                  {showBatchProbLossForm && selectedUsers.size > 0 && (
                    <div className={styles.batchProbLossForm}>
                      <div className={styles.batchProbLossHeader}>
                        <span className={styles.batchProbLossTitle}>
                          Configure ProbLoss for {selectedUsers.size} selected user{selectedUsers.size !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Rule Type Selection */}
                      <div className={styles.formGroup}>
                        <div className={styles.formLabel}>
                          <span className={styles.labelText}>Rule Type <span className={styles.labelRequired}>*</span></span>
                        </div>
                        <div className={styles.radioGroup}>
                          <label className={styles.radioLabel}>
                            <div className={styles.radioLabelMain}>
                              <input
                                type="radio"
                                value="row_based"
                                checked={batchProbLossForm.ruleType === 'row_based'}
                                onChange={(e) => updateBatchProbLossField('ruleType', e.target.value)}
                                disabled={batchProbLossLoading}
                              />
                              <span className={styles.radioLabelText}>Row Based</span>
                            </div>
                          </label>
                          <label className={styles.radioLabel}>
                            <div className={styles.radioLabelMain}>
                              <input
                                type="radio"
                                value="multiplier_based"
                                checked={batchProbLossForm.ruleType === 'multiplier_based'}
                                onChange={(e) => updateBatchProbLossField('ruleType', e.target.value)}
                                disabled={batchProbLossLoading}
                              />
                              <span className={styles.radioLabelText}>Multiplier Based</span>
                            </div>
                          </label>
                          <label className={styles.radioLabel}>
                            <div className={styles.radioLabelMain}>
                              <input
                                type="radio"
                                value="both"
                                checked={batchProbLossForm.ruleType === 'both'}
                                onChange={(e) => updateBatchProbLossField('ruleType', e.target.value)}
                                disabled={batchProbLossLoading}
                              />
                              <span className={styles.radioLabelText}>Both</span>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Row Threshold */}
                      {(batchProbLossForm.ruleType === 'row_based' || batchProbLossForm.ruleType === 'both') && (
                        <div className={styles.formGroup}>
                          <div className={styles.formLabel}>
                            <span className={styles.labelText}>Row Threshold <span className={styles.labelRequired}>*</span></span>
                          </div>
                          <input
                            type="number"
                            value={batchProbLossForm.rowThreshold ?? ''}
                            onChange={(e) => updateBatchProbLossField('rowThreshold', parseInt(e.target.value) || 0)}
                            onWheel={(e) => e.currentTarget.blur()}
                            min="0"
                            step="1"
                            placeholder="e.g., 3"
                            className={styles.input}
                            disabled={batchProbLossLoading}
                          />
                        </div>
                      )}

                      {/* Multiplier Threshold */}
                      {(batchProbLossForm.ruleType === 'multiplier_based' || batchProbLossForm.ruleType === 'both') && (
                        <div className={styles.formGroup}>
                          <div className={styles.formLabel}>
                            <span className={styles.labelText}>Multiplier Threshold <span className={styles.labelRequired}>*</span></span>
                          </div>
                          <input
                            type="number"
                            value={batchProbLossForm.multiplierThreshold ?? ''}
                            onChange={(e) => updateBatchProbLossField('multiplierThreshold', parseFloat(e.target.value) || 0)}
                            onWheel={(e) => e.currentTarget.blur()}
                            min="0"
                            step="0.1"
                            placeholder="e.g., 15.0"
                            className={styles.input}
                            disabled={batchProbLossLoading}
                          />
                        </div>
                      )}

                      {/* Loss Probability */}
                      <div className={styles.formGroup}>
                        <div className={styles.formLabel}>
                          <span className={styles.labelText}>Loss Probability <span className={styles.labelRequired}>*</span></span>
                        </div>
                        <input
                          type="number"
                          value={batchProbLossForm.lossProbability}
                          onChange={(e) => updateBatchProbLossField('lossProbability', parseFloat(e.target.value) || 0)}
                          onWheel={(e) => e.currentTarget.blur()}
                          min="0"
                          max="1"
                          step="0.01"
                          placeholder="e.g., 0.6"
                          className={styles.input}
                          disabled={batchProbLossLoading}
                        />
                        <div className={styles.probabilityDisplay}>
                          {(batchProbLossForm.lossProbability * 100).toFixed(1)}% chance
                        </div>
                      </div>

                      {/* Notes */}
                      <div className={styles.formGroup}>
                        <div className={styles.formLabel}>
                          <span className={styles.labelText}>Notes (Optional)</span>
                        </div>
                        <textarea
                          value={batchProbLossForm.notes || ''}
                          onChange={(e) => updateBatchProbLossField('notes', e.target.value)}
                          placeholder="Add notes about this configuration..."
                          maxLength={200}
                          rows={2}
                          className={styles.textarea}
                          disabled={batchProbLossLoading}
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className={styles.formActions}>
                        <button
                          onClick={handleBatchProbLossUpdate}
                          disabled={batchProbLossLoading}
                          className={`btn ${batchProbLossLoading ? styles.btnLoading : styles.btnPrimary}`}
                        >
                          <span className={styles.btnIcon}>{batchProbLossLoading ? '⏳' : '💾'}</span>
                          <span className={styles.btnText}>
                            {batchProbLossLoading ? 'Updating...' : `Apply to ${selectedUsers.size} Users`}
                          </span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Users Table */}
                  {referredUsersData.referredUsers.length > 0 ? (
                    <div className={styles.referredUsersTable}>
                      <table>
                        <thead>
                          <tr>
                            <th className={styles.checkboxColumn}></th>
                            <th>Wallet</th>
                            <th>Mark</th>
                            <th>Registered</th>
                            <th>Games</th>
                            <th>ProbLoss</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referredUsersData.referredUsers.map((user: ReferredUserWithStats) => (
                            <tr
                              key={user.walletAddress}
                              className={selectedUsers.has(user.walletAddress) ? styles.selectedRow : ''}
                            >
                              <td className={styles.checkboxColumn}>
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.has(user.walletAddress)}
                                  onChange={() => handleToggleUserSelection(user.walletAddress)}
                                />
                              </td>
                              <td>
                                <code className={styles.walletCode}>
                                  {user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}
                                </code>
                              </td>
                              <td>
                                {user.mark ? (
                                  <span className={`${styles.statusBadge} ${styles.mark}`}>
                                    🏷️ {user.mark}
                                  </span>
                                ) : (
                                  <span className={styles.noData}>-</span>
                                )}
                              </td>
                              <td>
                                <span className={styles.dateCell}>
                                  {formatDateAgo(user.createdAt)}
                                </span>
                              </td>
                              <td>
                                <span className={styles.gamesCount}>{user.gamesCount}</span>
                              </td>
                              <td>
                                {user.probLossConfig ? (
                                  <span className={`${styles.statusBadge} ${styles.hasProbLoss}`}>
                                    {user.probLossConfig.ruleType === 'row_based' && `📊 Row≥${user.probLossConfig.rowThreshold}`}
                                    {user.probLossConfig.ruleType === 'multiplier_based' && `📈 >${user.probLossConfig.multiplierThreshold}x`}
                                    {user.probLossConfig.ruleType === 'both' && '🎯 Both'}
                                    {' '}({(user.probLossConfig.lossProbability * 100).toFixed(0)}%)
                                  </span>
                                ) : (
                                  <span className={styles.noData}>None</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className={styles.noReferredUsers}>
                      <span className={styles.noDataIcon}>👥</span>
                      <span className={styles.noDataText}>No referred users found</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default GameManagement;